import { useEffect, useRef, useState } from 'react';
import { sendEnquiry } from '../models/enquiriesApi';
import { attemptKey, loadAttempt, createAttempt, saveAttempt, acceptResult, rejectResult, canClear, submittedCount } from '../models/enquiryAttempt';

const flights = new Map();
export function useEnquiryAttempt(user, query, open, onCompleted, onFailure) {
  const scope = attemptKey(user, query);
  const [state, setState] = useState({ scope, attempt: null, busy: false });
  const current = useRef({});
  current.current = { scope, open, onCompleted, onFailure };
  useEffect(() => {
    const read = () => setState({ scope, attempt: loadAttempt(user, query), busy: flights.has(scope) });
    read(); window.addEventListener('storage', read); window.addEventListener('enquiry-attempt-updated', read);
    return () => { if (current.current.scope === scope) current.current = {}; window.removeEventListener('storage', read); window.removeEventListener('enquiry-attempt-updated', read); };
  }, [scope, user, query]);
  async function execute(saved) {
    if (flights.has(scope)) return flights.get(scope);
    let release;
    const flight = new Promise(resolve => { release = resolve; });
    flights.set(scope, flight);
    setState({ scope, attempt: saved, busy: true });
    let next = saved;
    try {
      // Frozen request is persisted before any network work. Failure closes sending.
      saveAttempt(saved);
      try { next = acceptResult(saved, await sendEnquiry(query, saved.payload, saved.idempotencyKey)); }
      catch (error) {
        next = rejectResult(saved, error);
        if (current.current.scope === scope && current.current.open) current.current.onFailure?.(error);
      }
      saveAttempt(next);
      if (current.current.scope === scope && current.current.open && canClear(next)) current.current.onCompleted?.(submittedCount(next));
    } catch {
      next = { ...saved, status: 'blocked', storageBlocked: true, error: 'Cannot persist this send safely. Do not send again until the saved outcome is verified.' };
    } finally {
      flights.delete(scope); release(next);
      if (current.current.scope === scope) setState({ scope, attempt: next, busy: false });
    }
    return next;
  }
  const attempt = state.scope === scope ? state.attempt : loadAttempt(user, query);
  async function startLocked(payload, team) {
    if (current.current.scope !== scope || !current.current.open) return false;
    const saved = loadAttempt(user, query);
    if (saved || flights.has(scope) || !user || !query) return false;
    await execute(createAttempt(user, query, payload, team)); return true;
  }
  // Secure-context Chrome/Firefox serialize tab-level check-and-create, so a
  // second tab cannot replace the first tab's frozen request with another key.
  function start(payload, team = []) {
    if (navigator.locks?.request) return navigator.locks.request(scope, { ifAvailable: true }, lock => lock ? startLocked(payload, team) : false);
    return startLocked(payload, team);
  }
  function check() {
    const saved = loadAttempt(user, query);
    if (saved && !saved.storageBlocked && !['completed', 'rejected'].includes(saved.status)) return execute(saved);
  }
  function clear() {
    const saved = loadAttempt(user, query);
    if (flights.has(scope) || saved?.storageBlocked || !(canClear(saved) || saved?.status === 'rejected')) return false;
    try { localStorage.removeItem(scope); window.dispatchEvent(new Event('enquiry-attempt-updated')); return true; } catch { return false; }
  }
  // Once per open/scope: reload recovery uses the exact persisted request, not a new UUID.
  useEffect(() => {
    const saved = loadAttempt(user, query);
    if (open && saved && !saved.storageBlocked && ['processing', 'unknown'].includes(saved.status)) execute(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, open]);
  return { attempt, busy: state.scope === scope && state.busy, start, check, clear };
}
